/**
 * VARLayer
 *
 * Layer for displaying Values at Risk (VAR) as interactive markers on the map.
 * Fetches data from /api/var and displays with data-driven styling by var_type.
 *
 * Supports filtered modes via config.filterMode:
 *   - undefined: All VARs rendered as circles (default)
 *   - 'red_flag': Only red-flagged VARs, rendered as flag icons
 *   - 'sprinkler': Only sprinkler-active VARs, rendered as shower icons
 *
 * Issue: #290, #334
 */

import mapboxgl from 'mapbox-gl';
import { BaseLayer, LayerConfig } from './BaseLayer';
import { getBackendUrl } from '../../services/configService';
import { VAREntity, VARCategory } from '../../types/var';
import { logger } from '../../utils/logger';
import { svgToImage, loadFaIconToMap } from './utils/iconUtils';
import { visibilityAwareInterval, VisibilityAwareIntervalHandle } from './utils/visibilityAwareInterval';
import { isInRefreshTimeRange } from './utils/refreshTimeRange';
import { VAR_SPRITE_IDS, VAR_SHAPE_SUFFIX, VAR_ZOOM } from './varSymbology';
import './VARLayer.scss';

const log = logger('VARLayer');

type FilterMode = 'red_flag' | 'sprinkler';

/**
 * Color mapping for VAR types
 */
export class VARLayer extends BaseLayer {
  private sourceId: string;
  private layerId: string;          // symbol+label layer (z >= dotToSymbol)
  private dotsLayerId: string;      // bare-dot circle layer (z < dotToSymbol)
  private clusterCircleLayerId: string;  // cluster aggregate circles
  private clusterCountLayerId: string;   // cluster count text
  private highlightLayerId: string;
  private geojsonData: GeoJSON.FeatureCollection | null = null;
  private refreshHandle: VisibilityAwareIntervalHandle | null = null;
  private clickHandler: ((e: mapboxgl.MapMouseEvent) => void) | null = null;
  private currentPopup: mapboxgl.Popup | null = null;
  private entityLookup: Map<string, VAREntity> = new Map();
  private flagToggledHandler: (() => void) | null = null;
  private viewOnMapHandler: ((e: Event) => void) | null = null;
  /** Temp pin shown for archived VARs / when the layer is off — cleaned up on popup close. */
  private viewMarker: mapboxgl.Marker | null = null;
  private spritesLoaded = false;
  /** FA `cloud-rain` icon id — matches the WFS Wx stations layer style. */
  private stationIconId = '';
  private filterClickHandler: ((e: mapboxgl.MapMouseEvent) => void) | null = null;
  private filterMode: FilterMode | undefined;
  private styleLoadHandler: (() => void) | null = null;

  constructor(map: mapboxgl.Map, config: LayerConfig) {
    super(map, config);
    this.sourceId = `var-source-${config.id}`;
    this.layerId = `var-layer-${config.id}`;
    this.dotsLayerId = `var-dots-${config.id}`;
    this.clusterCircleLayerId = `var-cluster-${config.id}`;
    this.clusterCountLayerId = `var-cluster-count-${config.id}`;
    this.highlightLayerId = `var-highlight-${config.id}`;
    this.filterMode = config.config?.filterMode as FilterMode | undefined;
  }

  protected async onInitialize(): Promise<void> {
    log.debug(`Initializing VAR layer: ${this.config.name} (filterMode: ${this.filterMode || 'none'})`);

    try {
      await this.fetchData();
      log.debug(`Initial data fetch successful for ${this.config.name}`);
    } catch (error) {
      log.warn(`Initial data fetch failed for ${this.config.name}, will retry on enable:`, error);
      this.geojsonData = { type: 'FeatureCollection', features: [] };
    }

    // "View on Map" only applies to the base (non-filtered) layer
    if (!this.filterMode) {
      this.viewOnMapHandler = (e: Event) => {
        const { entityId, entity } = (e as CustomEvent).detail;
        this.flyToAndOpenPopup(entityId, entity);
      };
      window.addEventListener('intellifire:var-view-on-map', this.viewOnMapHandler);
    }
  }

  protected async onEnable(): Promise<void> {
    log.debug(`Enabling VAR layer: ${this.config.name}`);

    // Style-load guard. If the user toggles a VAR layer before the basemap
    // style has resolved, addLayer / addSource calls fail silently and the
    // Red Flag / Sprinkler icons never appear. `idle` is a guaranteed-once
    // post-paint signal where isStyleLoaded() === true.
    if (!this.map.isStyleLoaded()) {
      this.map.once('idle', () => {
        this.onEnable().catch(err => log.error('VARLayer deferred onEnable failed:', err));
      });
      return;
    }

    // Rehydrate on background switch: Mapbox's setStyle diff bails to a
    // full rebuild whenever sprite/glyphs change (any basemap swap), which
    // wipes the per-style image cache AND drops our overlay layers/sources.
    // Doing this re-add SYNCHRONOUSLY inside the style.load callback (before
    // any await yields the microtask queue) means the icons + source + layer
    // are present on Mapbox's first paint of the new style — no visible
    // flash. Falls through to a full async onEnable() for anything that
    // can't be reconstituted synchronously (e.g. uncached icons after a
    // hot reload).
    if (!this.styleLoadHandler) {
      this.styleLoadHandler = () => {
        if (!this.state.isEnabled) return;
        this.rehydrateOnStyleLoad();
      };
      this.map.on('style.load', this.styleLoadHandler);
    }

    // Load the 12 VAR sprites + 1 station teardrop into the map's image atlas.
    await this.loadVARSprites();

    // Fetch data if we don't have any
    if (!this.geojsonData || this.geojsonData.features.length === 0) {
      try {
        await this.fetchData();
      } catch (error) {
        log.error(`Failed to fetch VAR data on enable:`, error);
        throw new Error(`Cannot enable layer without data: ${error instanceof Error ? error.message : String(error)}`);
      }
    }

    // Source is shared across base and filter modes. Clustering is enabled
    // so dense urban areas (Yellowknife, Hay River) collapse to a single
    // count badge instead of stacked overlapping shapes. clusterMaxZoom
    // matches dotToSymbol so by the time symbols appear, clusters have
    // already broken apart into individual points.
    if (!this.map.getSource(this.sourceId)) {
      this.map.addSource(this.sourceId, {
        type: 'geojson',
        data: this.geojsonData || { type: 'FeatureCollection', features: [] },
        cluster: true,
        // Clusters persist all the way to z 12 so dense river/town corridors
        // (well past the dotToSymbol breakpoint at 6 and the label breakpoint
        // at 9) still aggregate. Individual symbols show simultaneously for
        // unclustered features; in dense areas clusters dominate, in sparse
        // areas individuals show. Gradual break-apart as the user zooms in.
        clusterMaxZoom: 11,
        clusterRadius: 20,
      });
    } else {
      const source = this.map.getSource(this.sourceId) as mapboxgl.GeoJSONSource;
      if (source && this.geojsonData) {
        source.setData(this.geojsonData);
      }
    }

    // Highlight ring sits between the dots layer (below) and symbols (above)
    // so the selected VAR shows the ring whether the user is at dot zoom or
    // symbol zoom. Empty filter until a feature is selected.
    if (!this.filterMode && !this.map.getLayer(this.highlightLayerId)) {
      this.map.addLayer({
        id: this.highlightLayerId,
        type: 'circle',
        source: this.sourceId,
        minzoom: this.config.zoom_min,
        maxzoom: this.config.zoom_max,
        paint: {
          'circle-color': 'rgba(0,0,0,0)',
          'circle-stroke-color': '#000000',
          'circle-stroke-width': 3,
          'circle-radius': 14,
        },
        filter: ['==', ['get', 'id'], ''],
      });
    }

    // Two layers: dots at low zoom, full shape/state symbols + ID label at
    // higher zoom. Filter modes apply the same symbology but with a filter
    // expression so flagged or sprinklered VARs render alone.
    const [dotsDef, symbolsDef] = this.buildLayerDefinitions();

    if (!this.map.getLayer(this.dotsLayerId)) {
      this.map.addLayer(dotsDef);
    } else {
      this.map.setLayoutProperty(this.dotsLayerId, 'visibility', 'visible');
    }
    if (!this.map.getLayer(this.layerId)) {
      this.map.addLayer(symbolsDef);
    } else {
      this.map.setLayoutProperty(this.layerId, 'visibility', 'visible');
      if (!this.filterMode && this.map.getLayer(this.highlightLayerId)) {
        this.map.setLayoutProperty(this.highlightLayerId, 'visibility', 'visible');
      }
    }

    this.addClusterLayers();

    if (this.filterMode) {
      this.setupFilterClickHandler();
    } else {
      this.setupClickHandler();
    }

    // Stale-window guard: the early "fetch if empty" check above keeps the
    // source non-empty for a snappy first render, but data captured at app
    // load can be hours stale by the time the user toggles the layer on.
    // Fire a background refresh so red-flag / sprinkler state catches up
    // without waiting for the next auto-refresh tick.
    this.refresh().catch((err) =>
      log.warn(`VAR layer ${this.config.name} onEnable refresh failed:`, err),
    );

    // Auto-refresh and flag-toggled listener apply to all modes
    const refreshInterval = this.config.config.refreshInterval;
    if (refreshInterval && refreshInterval > 0) {
      this.startAutoRefresh(refreshInterval);
    }

    this.setupFlagToggledListener();

    log.debug(`VAR layer ${this.config.name} enabled successfully`);
  }

  /**
   * Synchronous fast path for style.load. Mapbox does a full style rebuild
   * on every basemap swap (its diff bails on sprite/glyph changes), which
   * wipes the per-style image cache. Running this all in the style.load
   * callback BEFORE yielding the microtask queue means the icons + source
   * + layer are present for Mapbox's first paint of the new style — no
   * visible flash. Only works when the icons are already in the global
   * iconCache (true after the first onEnable in the session); if anything
   * is missing we fall through to the slower async onEnable path.
   */
  private rehydrateOnStyleLoad(): void {
    // Sprites are PNG/SVG <img> elements registered with map.addImage. They
    // get wiped on style.load (Mapbox style diff bails to a full rebuild on
    // sprite changes). Simplest reliable path is the async re-enable — the
    // sprite loader is idempotent and fast (12 small SVGs cached by the
    // browser HTTP layer).
    this.spritesLoaded = false;
    this.onEnable().catch(err => log.error('VARLayer style.load rehydrate failed:', err));
  }

  protected async onDisable(): Promise<void> {
    for (const id of [this.dotsLayerId, this.layerId, this.clusterCircleLayerId, this.clusterCountLayerId]) {
      if (this.map.getLayer(id)) {
        this.map.setLayoutProperty(id, 'visibility', 'none');
      }
    }
    if (!this.filterMode && this.map.getLayer(this.highlightLayerId)) {
      this.map.setLayoutProperty(this.highlightLayerId, 'visibility', 'none');
    }
    if (this.filterMode) {
      this.cleanupFilterClickHandler();
    } else {
      this.cleanupClickHandler();
    }
    this.stopAutoRefresh();
    this.cleanupFlagToggledListener();
    if (this.styleLoadHandler) {
      this.map.off('style.load', this.styleLoadHandler);
      this.styleLoadHandler = null;
    }
  }

  protected async onSetOpacity(opacity: number): Promise<void> {
    if (!this.map.getLayer(this.layerId)) return;
    this.map.setPaintProperty(this.layerId, 'icon-opacity', opacity);
  }

  protected onSetOrder(order: number): void {
    // Move layer to appropriate position
  }

  protected async onDestroy(): Promise<void> {
    this.stopAutoRefresh();
    this.cleanupClickHandler();
    this.cleanupFilterClickHandler();
    this.cleanupFlagToggledListener();
    if (this.styleLoadHandler) {
      this.map.off('style.load', this.styleLoadHandler);
      this.styleLoadHandler = null;
    }

    if (this.viewOnMapHandler) {
      window.removeEventListener('intellifire:var-view-on-map', this.viewOnMapHandler);
      this.viewOnMapHandler = null;
    }

    for (const id of [this.layerId, this.dotsLayerId, this.clusterCountLayerId, this.clusterCircleLayerId, this.highlightLayerId]) {
      if (this.map.getLayer(id)) {
        this.map.removeLayer(id);
      }
    }
    if (this.map.getSource(this.sourceId)) {
      this.map.removeSource(this.sourceId);
    }

    // Sprites are shared across all VAR layer instances and live on the map
    // image atlas; we don't remove them from the map.
    this.spritesLoaded = false;
    this.geojsonData = null;
  }

  public getMapboxLayerIds(): string[] {
    // Order matters for the LayerRegistry reorder pass — bottom to top:
    // cluster circles → cluster counts → unclustered dots → symbol+labels.
    return [this.clusterCircleLayerId, this.clusterCountLayerId, this.dotsLayerId, this.layerId];
  }

  public getMapboxSourceIds(): string[] {
    return [this.sourceId];
  }

  /**
   * Manually refresh layer data
   */
  public async refresh(): Promise<void> {
    // Guard against the auto-refresh timer firing during basemap swaps or
    // layer teardown — map internals go undefined for a moment and calls
    // like marker.addTo() / map.getSource() crash.
    if (!this.state.isEnabled) return;
    if (!this.map || !this.map.isStyleLoaded()) return;

    await this.fetchData();

    if (!this.state.isEnabled || !this.map?.isStyleLoaded()) return;

    const source = this.map.getSource(this.sourceId) as mapboxgl.GeoJSONSource;
    if (source && this.geojsonData) {
      source.setData(this.geojsonData);
    }
  }

  /**
   * Load the 12 VAR shape/state sprites + the WFS-matching FA `cloud-rain`
   * icon for stations into the map's image atlas. Idempotent across instances;
   * the first VAR layer enabled pays the cost, the rest are no-ops via
   * map.hasImage().
   */
  private async loadVARSprites(): Promise<void> {
    if (this.spritesLoaded) return;
    // FA cloud-rain — same icon + colour the WFS Wx stations layer uses, so
    // stations on the VAR layer match the rest of the app.
    try {
      this.stationIconId = await loadFaIconToMap(this.map, 'cloud-rain', {
        color: '#6b7280',
        size: 1.0,
        haloColor: '#ffffff',
        haloWidth: 0.5,
      });
    } catch (err) {
      log.warn('Failed to load FA cloud-rain icon for stations:', err);
    }
    // mapbox-gl-js `loadImage(url)` does not reliably handle SVG content from
    // an HTTP URL (it expects raster). The proven path is to fetch the SVG
    // text and rasterise via the existing svgToImage helper (`<img>` element
    // pointed at a data: URL). Same flow loadFaIconToMap uses for FA icons.
    const loads = Object.entries(VAR_SPRITE_IDS).map(async ([spriteId, url]) => {
      if (this.map.hasImage(spriteId)) return;
      try {
        const resp = await fetch(url);
        if (!resp.ok) {
          log.warn(`Failed to fetch sprite ${spriteId} from ${url}: HTTP ${resp.status}`);
          return;
        }
        const svgText = await resp.text();
        const img = await svgToImage(svgText);
        if (!this.map.hasImage(spriteId)) {
          this.map.addImage(spriteId, img, { sdf: false });
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        if (!msg.includes('already exists')) {
          log.warn(`loadVARSprites(${spriteId}) failed:`, err);
        }
      }
    });
    await Promise.all(loads);
    this.spritesLoaded = true;
  }

  /**
   * Fetch VAR data from API
   */
  private async fetchData(): Promise<void> {
    const backendUrl = getBackendUrl() || '';
    const params = new URLSearchParams();
    params.set('limit', '10000');
    // Map layer only shows Active VARs — Archived ones still live in the
    // VAR Explorer but shouldn't clutter the map.
    params.set('status', 'Active');

    if (this.filterMode === 'red_flag') {
      params.set('red_flag_active', 'true');
    } else if (this.filterMode === 'sprinkler') {
      params.set('sprinkler_active', 'true');
    }

    const apiUrl = `${backendUrl}/api/var?${params.toString()}`;
    // Weather stations join the same layer as teardrop icons. Only the
    // base (non-filtered) layer pulls them — the Red Flag / Sprinkler
    // filter modes are VAR-only by definition.
    // Stations are stateless — no Active/Archived concept. Filtering by
     // status=Active dropped every row in some envs.
    const stationsUrl = this.filterMode
      ? null
      : `${backendUrl}/api/weather-stations?limit=10000`;

    log.debug(`Fetching VAR data from: ${apiUrl}`);

    try {
      const [varResp, stationsResp] = await Promise.all([
        fetch(apiUrl, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }),
        stationsUrl
          ? fetch(stationsUrl, {
              method: 'GET',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
            }).catch(() => null)
          : Promise.resolve(null),
      ]);

      if (!varResp.ok) {
        throw new Error(`API request failed: ${varResp.statusText}`);
      }

      const result = await varResp.json();
      const stationsResult = stationsResp && stationsResp.ok ? await stationsResp.json() : null;
      const stationRows: any[] = stationsResult?.data ?? [];

      // Handle API response format: { success: true, data: VAREntity[] }
      const varEntities: VAREntity[] = result.data || result;

      // Build entity lookup for popup interactions
      this.entityLookup.clear();
      for (const entity of varEntities) {
        this.entityLookup.set(entity.id, entity);
      }

      // Convert to GeoJSON FeatureCollection. `flagged` and `sprinklered`
      // are derived so the MapLibre style expression in buildLayerDefinitions
      // stays a flat ['get', 'flagged'] / ['get', 'sprinklered'].
      const varFeatures: GeoJSON.Feature[] = varEntities
          .filter(entity => {
            const loc = entity.location as GeoJSON.Point | null;
            if (!loc || !Array.isArray(loc.coordinates)) return false;
            const [lon, lat] = loc.coordinates;
            if (typeof lon !== 'number' || typeof lat !== 'number') return false;
            if (!Number.isFinite(lon) || !Number.isFinite(lat)) return false;
            if (lon === 0 && lat === 0) return false;
            // Belt-and-braces — the API should already be filtering on status=Active,
            // but if archived rows leak through (cluster shows "16" with only 3
            // features visible at high zoom), drop them here too.
            if (entity.status && entity.status !== 'Active') return false;
            return true;
          })
          .map(entity => ({
            type: 'Feature' as const,
            geometry: entity.location as GeoJSON.Point,
            properties: {
              kind: 'var',
              id: entity.id,
              var_id: entity.var_id,
              label: entity.var_id,        // shared label property — also drives stations
              name: entity.name,
              var_type: entity.var_type,
              status: entity.status,
              region: entity.region,
              occupancy: entity.occupancy,
              flagged: !!entity.red_flag_active,
              sprinklered: entity.sprinklerFlag?.active ?? false,
              red_flag_active: entity.red_flag_active,
              red_flag_reason: entity.red_flag_reason,
              red_flag_status: entity.red_flag_status,
              red_flag_date: entity.red_flag_date,
              sprinkler_active: entity.sprinklerFlag?.active ?? false,
              sprinkler_name: entity.sprinklerFlag?.name ?? '',
              sprinkler_reason: entity.sprinklerFlag?.reason ?? '',
              sprinkler_date: entity.sprinklerFlag?.sprinkerlocaldate ?? ''
            }
          }));

      // Stations share the layer source. Discriminator `kind: 'station'`
      // drives the icon-image expression to the teardrop sprite and uses
      // the station's name as the label.
      const stationFeatures: GeoJSON.Feature[] = stationRows
        // Drop stations missing geometry or sitting at (0,0) — those default
        // coords cause every misconfigured station to stack into one phantom
        // cluster on the river instead of staying scattered at their real
        // locations.
        .filter((s: any) => {
          if (!s || !s.geometry || !Array.isArray(s.geometry.coordinates)) return false;
          const [lon, lat] = s.geometry.coordinates;
          if (typeof lon !== 'number' || typeof lat !== 'number') return false;
          if (!Number.isFinite(lon) || !Number.isFinite(lat)) return false;
          if (lon === 0 && lat === 0) return false;
          return true;
        })
        .map((s: any) => ({
          type: 'Feature' as const,
          geometry: s.geometry as GeoJSON.Point,
          properties: {
            kind: 'station',
            id: `station-${s.id ?? s.nesdis ?? s.station_id ?? ''}`,
            label: s.station_name ?? s.name ?? s.nesdis ?? '',
            station_id: s.station_id ?? s.nesdis ?? null,
            // Stations have no flag/sprinkler state — set them so the
            // expression `case` falls through to '0' (no-op for stations
            // since the teardrop sprite is one variant only).
            flagged: false,
            sprinklered: false,
          },
        }));

      // Collapse exact-coordinate duplicates. If multiple features share the
      // same lat/lon (archived rows that leaked the status filter, stations
      // sharing a default coord, etc.), Mapbox clusters them into a single
      // "16" badge that never breaks apart at any zoom. Keep the first hit
      // per coord — VARs first so a VAR wins over a station at the same point.
      const seen = new Set<string>();
      const dedup = (feat: GeoJSON.Feature): boolean => {
        const c = (feat.geometry as GeoJSON.Point).coordinates;
        const key = `${c[0].toFixed(6)},${c[1].toFixed(6)}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      };
      this.geojsonData = {
        type: 'FeatureCollection',
        features: [...varFeatures.filter(dedup), ...stationFeatures.filter(dedup)],
      };

      log.debug(`Fetched ${this.geojsonData.features.length} VAR features`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to fetch VAR data: ${errorMessage}`);
    }
  }

  // ─── Filter mode rendering (red_flag / sprinkler) ───────────────────
  // Reuses the same sprite/shape grammar as the base layer via the same
  // buildLayerDefinitions() output. A Mapbox filter expression (applied
  // inside buildLayerDefinitions) restricts rendering to flagged-only or
  // sprinklered-only features so the red border / blue droplet visual is
  // automatically present.

  private setupFilterClickHandler(): void {
    if (this.filterClickHandler) return;
    this.filterClickHandler = (e) => {
      const features = this.map.queryRenderedFeatures(e.point, {
        layers: [this.layerId],
      });
      if (!features || features.length === 0) return;
      const feature = features[0];
      const coords = (feature.geometry as GeoJSON.Point).coordinates as [number, number];
      const popup = this.buildMarkerPopup(feature.properties || {});
      popup.setLngLat(coords).addTo(this.map);
    };
    this.map.on('click', this.layerId, this.filterClickHandler);
    this.map.on('mouseenter', this.layerId, () => {
      this.map.getCanvas().style.cursor = 'pointer';
    });
    this.map.on('mouseleave', this.layerId, () => {
      this.map.getCanvas().style.cursor = '';
    });
  }

  private cleanupFilterClickHandler(): void {
    if (this.filterClickHandler && this.map.getLayer(this.layerId)) {
      this.map.off('click', this.layerId, this.filterClickHandler);
    }
    this.filterClickHandler = null;
  }

  /**
   * Build the filter-mode popup. Adds a "Details" button that fires
   * `intellifire:var-view-on-map` so the base VAR layer (when enabled)
   * flies to the feature and opens its full React popup.
   */
  private buildMarkerPopup(props: Record<string, any>): mapboxgl.Popup {
    let flagInfo = '';
    if (this.filterMode === 'red_flag') {
      const dateStr = props.red_flag_date
        ? new Date(props.red_flag_date).toLocaleDateString()
        : 'N/A';
      flagInfo = `
        <div class="var-popup__row">
          <span class="var-popup__label">Flag Status:</span>
          <span class="var-popup__value">${props.red_flag_status || 'Active'}</span>
        </div>
        <div class="var-popup__row">
          <span class="var-popup__label">Activated:</span>
          <span class="var-popup__value">${dateStr}</span>
        </div>
        ${props.red_flag_reason ? `
        <div class="var-popup__row">
          <span class="var-popup__label">Reason:</span>
          <span class="var-popup__value">${props.red_flag_reason}</span>
        </div>` : ''}`;
    } else {
      const dateStr = props.sprinkler_date
        ? new Date(props.sprinkler_date).toLocaleDateString()
        : 'N/A';
      flagInfo = `
        ${props.sprinkler_name ? `
        <div class="var-popup__row">
          <span class="var-popup__label">Sprinkler:</span>
          <span class="var-popup__value">${props.sprinkler_name}</span>
        </div>` : ''}
        <div class="var-popup__row">
          <span class="var-popup__label">Deployed:</span>
          <span class="var-popup__value">${dateStr}</span>
        </div>
        ${props.sprinkler_reason ? `
        <div class="var-popup__row">
          <span class="var-popup__label">Reason:</span>
          <span class="var-popup__value">${props.sprinkler_reason}</span>
        </div>` : ''}`;
    }

    const entityId = (props.id as string | undefined) ?? '';
    const html = `
      <div class="var-popup">
        <h4 class="var-popup__title">${props.name || 'Unknown'}</h4>
        <div class="var-popup__row">
          <span class="var-popup__label">Type:</span>
          <span class="var-popup__value">${props.var_type || 'N/A'}</span>
        </div>
        <div class="var-popup__row">
          <span class="var-popup__label">VAR ID:</span>
          <span class="var-popup__value">${props.var_id || 'N/A'}</span>
        </div>
        ${flagInfo}
        <div class="var-popup__actions">
          <button type="button"
                  class="var-popup__details-btn"
                  data-var-entity-id="${entityId}">
            Details
          </button>
        </div>
      </div>`;

    const popup = new mapboxgl.Popup({
      closeButton: true,
      closeOnClick: true,
      className: 'var-feature-popup',
      offset: 15,
    }).setHTML(html);

    // Wire the Details button after the popup mounts into the DOM. Clicking
    // it dispatches `intellifire:var-view-on-map`; the base VAR layer (if
    // enabled) listens for that event and opens its full React popup.
    popup.on('open', () => {
      const popupEl = popup.getElement();
      if (!popupEl) return;
      const el = popupEl.querySelector('.var-popup__details-btn');
      if (!el) return;
      el.addEventListener('click', () => {
        if (entityId) {
          window.dispatchEvent(new CustomEvent('intellifire:var-view-on-map', {
            detail: { entityId },
          }));
        }
        popup.remove();
      });
    });

    return popup;
  }

  // ─── Circle layer rendering (base mode) ───────────────────────────

  /**
   * Build the two layer definitions for VAR symbology:
   *   1. Dots (circle) — visible below the dot→symbol zoom breakpoint.
   *   2. Symbols + ID label — sprite icon driven by shape+state, with the
   *      VAR ID text appearing only at or above the label zoom breakpoint.
   *
   * Filter mode (red_flag / sprinkler) layers reuse the same definitions
   * but apply a Mapbox filter so only flagged or sprinklered VARs render.
   */
  /**
   * Add the cluster aggregate layers (circle + count text). Clusters
   * only exist below clusterMaxZoom (= dotToSymbol) — at higher zoom
   * everything is unclustered and these layers render nothing.
   */
  private addClusterLayers(): void {
    // Re-enable path: layers already exist from a prior enable cycle —
    // they were just set invisible by onDisable. Flip them back on.
    if (this.map.getLayer(this.clusterCircleLayerId)) {
      this.map.setLayoutProperty(this.clusterCircleLayerId, 'visibility', 'visible');
      if (this.map.getLayer(this.clusterCountLayerId)) {
        this.map.setLayoutProperty(this.clusterCountLayerId, 'visibility', 'visible');
      }
      return;
    }
    // Color/size ramp by cluster size: small / medium / large.
    this.map.addLayer({
      id: this.clusterCircleLayerId,
      type: 'circle',
      source: this.sourceId,
      filter: ['has', 'point_count'],
      minzoom: this.config.zoom_min ?? 0,
      // No maxzoom — the cluster source stops producing clusters past
      // clusterMaxZoom, so this layer naturally has nothing to render past
      // that point. Setting an explicit maxzoom (12 / 11) caused a one-zoom
      // blank band right at the boundary because Mapbox's cluster index
      // briefly emits point_count=1 features that the symbol layer filter
      // (`!has(point_count)`) then excluded.
      paint: {
        // Smooth gradient — light/small at the low end (5 or fewer points),
        // dark/large at the high end (100+). Makes the count readable from
        // size+colour alone, not just the number.
        // Inverse-log feel — bigger differentiation at low counts,
        // gentle plateau near the high end. Tuned with three stops
        // instead of exponential interpolation so 2 reads clearly and
        // 100 doesn't dwarf the rest. Min radius 12 keeps small clusters
        // readable; min color grey-500 is dark enough to spot.
        'circle-color': [
          'interpolate', ['linear'], ['get', 'point_count'],
          2,   '#9ca3af',   // grey-400 (20% lighter than grey-500)
          20,  '#4b5563',   // grey-600
          100, '#1f2937',   // grey-800
        ],
        'circle-radius': [
          'interpolate', ['linear'], ['get', 'point_count'],
          2,   12,
          20,  20,
          100, 26,
        ],
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 1.5,
        'circle-opacity': this.state.currentOpacity,
      },
    });
    this.map.addLayer({
      id: this.clusterCountLayerId,
      type: 'symbol',
      source: this.sourceId,
      filter: ['has', 'point_count'],
      minzoom: this.config.zoom_min ?? 0,
      // No maxzoom — the cluster source stops producing clusters past
      // clusterMaxZoom, so this layer naturally has nothing to render past
      // that point. Setting an explicit maxzoom (12 / 11) caused a one-zoom
      // blank band right at the boundary because Mapbox's cluster index
      // briefly emits point_count=1 features that the symbol layer filter
      // (`!has(point_count)`) then excluded.
      layout: {
        'text-field': ['get', 'point_count_abbreviated'],
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': 12,
        'text-allow-overlap': true,
      },
      paint: {
        'text-color': '#ffffff',
      },
    });
  }

  private buildLayerDefinitions(): [mapboxgl.CircleLayer, mapboxgl.SymbolLayer] {
    // Cluster layers are added separately in addClusterLayers(); the
    // dot/symbol layers below filter on `!has(point_count)` so they only
    // render unclustered (leaf) features.
    // Shape suffix from VAR category. Falls back to 'sq' (Infrastructure)
    // for unknown categories so the icon-image expression never fails.
    const shapeExpr: any = [
      'match',
      ['get', 'var_type'],
      'Infrastructure',     VAR_SHAPE_SUFFIX['Infrastructure'],
      'Cultural Resource',  VAR_SHAPE_SUFFIX['Cultural Resource'],
      'Natural Resource',   VAR_SHAPE_SUFFIX['Natural Resource'],
      VAR_SHAPE_SUFFIX['Infrastructure'],   // fallback to triangle
    ];

    // State suffix: '0' = none, 's' = sprinklered, 'f' = flagged, 'sf' = both.
    const stateExpr: any = [
      'case',
      ['all', ['get', 'flagged'], ['get', 'sprinklered']], 'sf',
      ['get', 'flagged'],     'f',
      ['get', 'sprinklered'], 's',
      '0',
    ];

    // Filter scoping. Always exclude cluster features (point_count is set on
    // them by Mapbox's clustering). Then layer on the filter-mode predicate
    // for the standalone Red Flag / Sprinkler layers.
    const filterParts: any[] = [['!', ['has', 'point_count']]];
    if (this.filterMode === 'red_flag') {
      filterParts.push(['==', ['get', 'flagged'], true]);
    } else if (this.filterMode === 'sprinkler') {
      filterParts.push(['==', ['get', 'sprinklered'], true]);
    }
    const filter: any = filterParts.length === 1 ? filterParts[0] : ['all', ...filterParts];

    // Dots — bare circles below the dot→symbol breakpoint. Cheap to render
    // at high zoom-out density.
    const dotsDef: mapboxgl.CircleLayer = {
      id: this.dotsLayerId,
      type: 'circle' as const,
      source: this.sourceId,
      minzoom: this.config.zoom_min ?? 0,
      maxzoom: VAR_ZOOM.dotToSymbol,
      filter,
      paint: {
        'circle-radius': 3,
        'circle-color': '#6b7280',
        'circle-stroke-width': 0.5,
        'circle-stroke-color': '#ffffff',
        'circle-opacity': this.state.currentOpacity,
      },
    };

    // Symbols + ID label. Single layer; text-field shows '' below the label
    // zoom and the var_id at/above it via a step expression.
    const symbolsDef: mapboxgl.SymbolLayer = {
      id: this.layerId,
      type: 'symbol' as const,
      source: this.sourceId,
      minzoom: VAR_ZOOM.dotToSymbol,
      maxzoom: this.config.zoom_max,
      filter,
      paint: {
        'icon-opacity': this.state.currentOpacity,
        'text-color': '#222222',
        'text-halo-color': '#ffffff',
        'text-halo-width': 1.5,
      },
      layout: {
        // Stations are plain teardrops with no state grammar — they're not
        // managed entities (no flag / sprinkler attachable). A station that
        // needs a flag or sprinkler becomes a VAR.
        'icon-image': [
          'case',
          ['==', ['get', 'kind'], 'station'],
          this.stationIconId,
          ['concat', 'var-', shapeExpr, '-', stateExpr],
        ] as any,
        // All four shapes (inverted triangle, diamond, pentagon, teardrop)
        // have their bottom-most point at the coordinate so 'bottom' anchor
        // makes the visual apex land on the actual location.
        'icon-anchor': 'bottom',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        // Zoom-scaled icon size; stations rendered ~2× the VAR size so the
        // cloud-with-rain glyph reads at the same visual weight. Mapbox
        // requires `zoom` expressions at the top level of interpolate, so
        // the per-kind size lives inside each stop's value via `case`.
        'icon-size': [
          'interpolate', ['linear'], ['zoom'],
          VAR_ZOOM.dotToSymbol, 0.6,
          12,                   1.0,
        ] as any,
        // VAR ID label appears at/above the label zoom breakpoint, above
        // the icon (icons are square-ish so 'top' anchor works for all 3
        // VAR shapes uniformly).
        // Both VARs and stations carry a `label` property — var_id for VARs,
        // station name for stations. One expression covers both.
        'text-field': ['step', ['zoom'], '', VAR_ZOOM.label, ['get', 'label']] as any,
        'text-anchor': 'bottom',
        // Icon is bottom-anchored. VAR icons render ~24px tall at z12, station
        // cloud renders ~48px tall (icon-size 2.0). Per-kind offset so the
        // label clears the top edge in both cases.
        'text-offset': [
          'case',
          ['==', ['get', 'kind'], 'station'],
          ['literal', [0, -3.0]],
          ['literal', [0, -1.7]],
        ] as any,
        'text-size': 11,
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
        'text-allow-overlap': false,
        // text-optional: when text can't be placed (collision OR empty step
        // value at z < label zoom), still render the icon. Without this the
        // whole symbol (icon + text) is dropped for empty labels, which is
        // why nothing showed between zoom 6 and 9.
        'text-optional': true,
        visibility: 'visible' as const,
      },
    };

    return [dotsDef, symbolsDef];
  }

  /**
   * Open a Mapbox popup with an empty React-portal target. VARPopupContent
   * (mounted once at the app root) listens for `varPopup:open` and
   * `varPopup:close` to render into / unmount from the container div.
   * Also highlights the selected VAR via the highlight layer's filter.
   */
  private openEntityPopup(entity: VAREntity, lngLat: [number, number]): void {
    if (this.currentPopup) {
      this.currentPopup.remove();
    }
    // If the VAR isn't currently rendered on the layer (archived, or the
    // layer is toggled off), drop a temp pin at the location so the popup
    // has something pointing at it.
    if (this.viewMarker) {
      this.viewMarker.remove();
      this.viewMarker = null;
    }
    const isRendered = this.state.isEnabled && entity.status === 'Active';
    if (!isRendered) {
      this.viewMarker = new mapboxgl.Marker({ color: '#6b7280' })
        .setLngLat(lngLat)
        .addTo(this.map);
    }

    // Pan the map so the feature lands at ~70% of the viewport height,
    // leaving room above for the popup to expand without running off the
    // top of the screen. The popup anchors to lngLat and slides with the
    // map during the ease, so it stays visually locked to the feature.
    const containerHeight = this.map.getContainer().clientHeight;
    this.map.easeTo({
      center: lngLat,
      offset: [0, containerHeight * 0.2],
      duration: 400,
    });

    const container = document.createElement('div');
    container.className = 'var-popup-portal';

    this.currentPopup = new mapboxgl.Popup({
      closeButton: true,
      closeOnClick: true,
      className: 'var-feature-popup',
      maxWidth: 'none',
      // 10px lift so the icon stays visible above (or below) the popup
      // rather than tucked under its edge.
      offset: 10,
    })
      .setLngLat(lngLat)
      .setDOMContent(container)
      .addTo(this.map);

    this.setHighlightedEntity(entity.id);

    this.currentPopup.on('close', () => {
      window.dispatchEvent(new CustomEvent('varPopup:close'));
      this.setHighlightedEntity(null);
      this.currentPopup = null;
      if (this.viewMarker) {
        this.viewMarker.remove();
        this.viewMarker = null;
      }
    });

    window.dispatchEvent(new CustomEvent('varPopup:open', {
      detail: { popupElement: container, entity },
    }));
  }

  private setHighlightedEntity(id: string | null): void {
    if (!this.map.getLayer(this.highlightLayerId)) return;
    this.map.setFilter(this.highlightLayerId, ['==', ['get', 'id'], id ?? '']);
  }

  /**
   * Set up click handler for feature popups (base mode only)
   */
  private setupClickHandler(): void {
    this.cleanupClickHandler();

    this.clickHandler = (e: mapboxgl.MapMouseEvent) => {
      const features = this.map.queryRenderedFeatures(e.point, {
        layers: [this.layerId]
      });

      if (!features || features.length === 0) return;

      const entityId = features[0].properties?.id as string | undefined;
      if (!entityId) return;

      const entity = this.entityLookup.get(entityId);
      if (!entity) return;

      // Anchor the popup to the feature's actual coordinate, not the click
      // location — at higher zooms the clickable hit-area can be 10s of px
      // from the icon, so e.lngLat would put the popup well off the icon.
      const featGeom = features[0].geometry as GeoJSON.Point;
      const coords = featGeom?.coordinates as [number, number] | undefined;
      this.openEntityPopup(entity, coords ?? [e.lngLat.lng, e.lngLat.lat]);
    };

    this.map.on('click', this.layerId, this.clickHandler);

    this.map.on('mouseenter', this.layerId, () => {
      this.map.getCanvas().style.cursor = 'pointer';
    });
    this.map.on('mouseleave', this.layerId, () => {
      this.map.getCanvas().style.cursor = '';
    });
  }

  /**
   * Fly to a VAR entity and open its popup. openEntityPopup() handles the
   * ease with the right offset for the popup to fit.
   */
  public flyToAndOpenPopup(entityId: string, entityOverride?: VAREntity): void {
    // Prefer the caller-supplied entity (explorer passes the full row, so
    // archived VARs work even though the layer only fetches status=Active).
    const entity = entityOverride ?? this.entityLookup.get(entityId);
    if (!entity || !entity.location) {
      log.warn(`Cannot fly to entity ${entityId}: not found or no location`);
      return;
    }
    const [lng, lat] = entity.location.coordinates;
    this.openEntityPopup(entity, [lng, lat]);
  }

  // ─── Shared lifecycle helpers ─────────────────────────────────────

  private setupFlagToggledListener(): void {
    this.cleanupFlagToggledListener();
    this.flagToggledHandler = () => {
      this.refresh().catch((err) => log.error('Failed to refresh after VAR data change:', err));
    };
    window.addEventListener('varFlagToggled', this.flagToggledHandler);
    window.addEventListener('varDataChanged', this.flagToggledHandler);
  }

  private cleanupFlagToggledListener(): void {
    if (this.flagToggledHandler) {
      window.removeEventListener('varFlagToggled', this.flagToggledHandler);
      window.removeEventListener('varDataChanged', this.flagToggledHandler);
      this.flagToggledHandler = null;
    }
  }

  private cleanupClickHandler(): void {
    if (this.clickHandler) {
      this.map.off('click', this.layerId, this.clickHandler);
      this.clickHandler = null;
    }
    if (this.currentPopup) {
      this.currentPopup.remove();
      this.currentPopup = null;
    }
  }

  private startAutoRefresh(interval: number): void {
    this.stopAutoRefresh();
    this.refreshHandle = visibilityAwareInterval(() => {
      const range = (this.config.config as any).refreshTimeRange;
      if (range && !isInRefreshTimeRange(range)) return;
      this.refresh().catch(err => log.error('Failed to refresh VARLayer:', err));
    }, interval);
  }

  private stopAutoRefresh(): void {
    if (this.refreshHandle) {
      this.refreshHandle.cancel();
      this.refreshHandle = null;
    }
  }
}
